import React, {useState} from 'react';
import {Material} from './types.ts';
import {Input, Label, FormGroup, Button, InputGroup} from 'reactstrap';
import {FileDataModel, Selection} from '../../api/types.ts';
import Icon from '../shared/Icon.tsx';
import {BanIcon, ImageIcon} from 'lucide-react';
import AssetSelectAndUploadModal from '../assets/AssetSelectAndUploadModal.tsx';
import {FileFormat} from '../../lib/FileFormats.ts';
import ColorPickerComponent from '../shared/ColorPicker.tsx';
import getColorHexValue from './getColorHexValue.ts';
import OptionalColorPicker from './OptionalColorPicker.tsx';

interface MaterialEditorProps {
    material: Material;
    onChange: (material: Material) => void;
}

// Edits the stored material directly (no local copy), and always sends it
// whole, so nothing from a previously selected object can leak into it.
const MaterialEditor: React.FC<MaterialEditorProps> = ({
    material,
    onChange
}) => {
    const change = (changes: Partial<Material>) =>
        onChange({...material, ...changes});

    const handleAlphaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        change({alpha: isNaN(value) ? undefined : value});
    };

    const [isImageSelectModalOpen, setIsImageSelectModalOpen] = useState(false);

    const onImageSelectionChanged = (files: FileDataModel[] | undefined) => {
        if (!files) {
            return;
        }

        if (files.length > 0) {
            change({texture: files[0].uri});
        }

        setIsImageSelectModalOpen(false);
    };

    return (
        <div>
            <FormGroup>
                <Label>Color:</Label>
                <ColorPickerComponent
                    color={getColorHexValue(material.color ?? '#ffffff')}
                    onChange={color => change({color})}
                />
            </FormGroup>
            <FormGroup>
                <Label>Texture:</Label>
                <InputGroup>
                    <Input
                        type="text"
                        value={material.texture ?? ''}
                        onChange={() => {}}
                        disabled
                    />

                    <Button
                        color="secondary"
                        onClick={() => setIsImageSelectModalOpen(true)}
                        aria-label="Select texture image"
                        title="Select texture image"
                    >
                        <Icon icon={ImageIcon} />
                    </Button>
                    {isImageSelectModalOpen && (
                        <AssetSelectAndUploadModal
                            options={{
                                selection: Selection.Single,
                                explanation: 'Please select an image file',
                                formats: FileFormat.Image,
                                fileSelectionChanged: onImageSelectionChanged
                            }}
                            onClose={() => setIsImageSelectModalOpen(false)}
                        />
                    )}

                    <Button
                        color="secondary"
                        onClick={() => change({texture: ''})}
                        title="Remove texture"
                    >
                        <Icon icon={BanIcon} />
                    </Button>
                </InputGroup>
            </FormGroup>
            <FormGroup>
                <Label>Alpha:</Label>
                <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={material.alpha ?? ''}
                    onChange={handleAlphaChange}
                />
            </FormGroup>
            <FormGroup>
                <Label>Emissive (glow):</Label>
                <OptionalColorPicker
                    color={material.emissive}
                    noneColor="#000000"
                    noneLabel="None"
                    noneTitle="No glow"
                    onChange={emissive => change({emissive})}
                />
            </FormGroup>
            <FormGroup>
                <Label>Specular (highlights):</Label>
                <OptionalColorPicker
                    color={material.specular}
                    noneColor="#ffffff"
                    noneLabel="Default"
                    noneTitle="Default highlights"
                    onChange={specular => change({specular})}
                />
            </FormGroup>
            <FormGroup check>
                <Label check>
                    <Input
                        type="checkbox"
                        checked={material.wireframe === true}
                        onChange={e => change({wireframe: e.target.checked})}
                    />{' '}
                    Wireframe
                </Label>
            </FormGroup>
        </div>
    );
};

export default MaterialEditor;
